import { IUserAddress, UserAddress } from '../../types/api-response/building/user-address';
import { IUserPhone, UserPhone } from '../../types/api-response/building/user-phone';
import { EmergencyContacts, IEmergencyContacts } from '../../types/api-response/building/emergency-contacts';
import { IVehicle, Vehicle } from '../../types/api-response/building/vehicle';
import { IBuilding } from '../../types/api-response/building';
import { IPet, Pet } from '../../types/api-response/building/pet';
import { IUser } from '../../types/api-response/building/user';
import { ISuite } from '../../types/api-response/building/suite';
import { ApiHelper } from '../../types/api-response/helpers';

export enum Solution {
    approve = 'approve',
    decline = 'decline'
}

/**
 * Resident patch changes item interface
 */
export interface IChanges {
    /**
     * Row title
     */
    title: string;

    /**
     * Whether item changed or not
     */
    changed: boolean;

    /**
     * Ignore item changes
     */
    ignore: boolean;

    /**
     * Whether item patched or not
     */
    patched: boolean;

    /**
     * Old data
     */
    oldData: any;

    /**
     * New data
     */
    newData: any;

    /**
     * Property managers solution (approved or declined)
     */
    solution: Solution;

    /**
     * Item type
     * (defines template for output)
     */
    type: string;

    getPatch(suite?: ISuite);
}

/**
 * Base item changes class
 */
export abstract class Changes<T> implements IChanges {
    title: string;
    changed = false;
    patched = false;
    ignore = false;
    oldData: T;
    newData: T;
    solution;
    type: string;

    protected static isChanged(oldData, newData) {
        return JSON.stringify(oldData) !== JSON.stringify(newData);
    }

    /**
     * Checks is source array
     * includes every item of subject array
     *
     * @param source
     * @param subject
     */
    protected static includesEvery(source: any[], subject: any[]) {
        if(!subject) {
            return true;
        }

        const sourcesJsonList = source.map(item => JSON.stringify(item));

        return subject.every(subjectEl => sourcesJsonList.includes(JSON.stringify(subjectEl)));
    }

    protected constructor(title: string, oldData: T, newData: T) {
        this.title = title;
        this.oldData = oldData;
        this.newData = newData;
        this.patched = typeof newData !== 'undefined' && newData !== null;
        this.changed = Changes.isChanged(this.oldData, this.newData);
        this.ignore = !this.patched || !this.changed;
        this.solution = null;
        this.type = null;
    }

    getPatch(suite?: ISuite): T {
        return this.newData;
    }
}

/**
 * Resident name changes
 */
export class NameChanges extends Changes<string> {
    constructor(title: string, oldData: string, newData: string) {
        super(title, oldData, newData);
        this.type = 'name';
    }
}


/**
 * Resident email changes
 */
export class EmailChanges extends Changes<string> {
    constructor(title: string, oldData: string, newData: string) {
        super(title, oldData, newData);
        this.type = 'email';
    }
}

/**
 * Resident photo changes
 */
export class PhotoChanges extends Changes<string> {
    constructor(title: string, oldData: string, newData: string) {
        super(title, oldData, newData);
        this.type = 'photo';
    }
}

/**
 * Resident groups changes
 */
export class GroupsChanges extends Changes<any> {
    constructor(title: string, oldData, newData) {
        super(title, oldData, newData);
        this.patched = Array.isArray(newData);
        this.changed = this.patched && Changes.isChanged(this.reduceData(oldData), this.reduceData(newData));
        this.ignore = !this.patched || !this.changed;
        this.type = 'groups';
    }

    getPatch(suite?: ISuite) {
        return (this.newData).map(row => {
            delete row.id;
            delete row.user;
            return {
                group: parseInt(row.group)
            };
        });
    }

    private reduceData(data) {
        return data.map((row) => {
            return {
                group: parseInt(row.group)
            };
        });
    }
}

/**
 * Resident address changes
 */
export class AddressChanges extends Changes<IUserAddress[]> {
    constructor(title: string, oldData: IUserAddress[], newData: IUserAddress[]) {
        super(title, oldData, newData);
        this.patched = Array.isArray(newData);
        this.changed = this.patched && Changes.isChanged(this.reduceData(oldData), this.reduceData(newData));
        this.ignore = !this.patched || !this.changed;
        this.type = 'address';
    }

    getPatch(suite?: ISuite): IUserAddress[] {
        return (this.newData as IUserAddress[]).map(address => {
            delete address.id;
            delete address.user;

            return ApiHelper.normalize(address, UserAddress);
        });
    }

    private reduceData(data: IUserAddress[]) {
        return data.map((row) => {
            const {address, city, country, state, postalCode, isPrimaryAddress} = row;
            return ApiHelper.normalize({address, city, country, state, postalCode, isPrimaryAddress}, UserAddress);
        });
    }
}

/**
 * Resident phone changes
 */
export class PhoneChanges extends Changes<IUserPhone[]> {
    constructor(title: string, oldData: IUserPhone[], newData: IUserPhone[]) {
        super(title, oldData, newData);
        this.patched = Array.isArray(newData);
        this.changed = this.patched && Changes.isChanged(this.reduceData(oldData), this.reduceData(newData));
        this.ignore = !this.patched || !this.changed;
        this.type = 'phone';
    }

    getPatch(suite?: ISuite): IUserPhone[] {
        return (this.newData as IUserPhone[]).map(phone => {
            delete phone.id;
            delete phone.user;

            return ApiHelper.normalize(phone, UserPhone);
        });
    }

    private reduceData(data: IUserPhone[]) {
        return data.map((row) => {
            const {phone, phoneType, isPrimaryPhone} = row;

            return new UserPhone({phone, phoneType, isPrimaryPhone});
        });
    }
}

/**
 * Resident birthday date changes
 */
export class DateChanges extends Changes<string> {
    constructor(title: string, oldData: string, newData: string) {
        super(title, oldData, newData);
        this.changed = Changes.isChanged(Date.parse(oldData).toString(), Date.parse(newData).toString());
        this.ignore = !this.patched || !this.changed;
        this.type = 'date';
    }
}

/**
 * Resident emergency contact changes
 */
export class EmergencyContactChanges extends Changes<IEmergencyContacts[]> {
    constructor(title: string, oldData: IEmergencyContacts[], newData: IEmergencyContacts[]) {
        super(title, oldData, newData);
        this.patched = Array.isArray(newData);
        this.changed = this.patched && Changes.isChanged(this.reduceData(oldData), this.reduceData(newData));
        this.ignore = !this.patched || !this.changed;
        this.type = 'emergencyContact';
    }

    private reduceData(data: IEmergencyContacts[]) {
        return data.map((row) => {
            const {name, phone, email, relation, note, isPrimary, suite} = row;

            return ApiHelper.normalize({name, phone, email, relation, note, isPrimary, suite}, EmergencyContacts);
        });
    }

    getPatch(suite?: ISuite): IEmergencyContacts[] {
        return (this.newData as IEmergencyContacts[]).map(emergencyContacts => {
            return ApiHelper.normalize(emergencyContacts, EmergencyContacts);
        });
    }
}

/**
 * Suite vehicle changes
 */
export class VehicleChanges extends Changes<IVehicle[]> {
    constructor(title: string, oldData: IVehicle[], newData: IVehicle[]) {
        super(title, oldData || [], newData || []);
        this.patched = Array.isArray(newData);
        this.changed = this.patched && Changes.isChanged(this.reduceData(oldData), this.reduceData(newData));
        this.ignore = !this.patched || !this.changed || Changes.includesEvery(this.reduceData(oldData), this.reduceData(newData));
        this.type = 'vehicle';
    }

    getPatch(suite: ISuite): IVehicle[] {
        return (this.newData as IVehicle[]).map(vehicle => {
            if(!vehicle.id) {
                vehicle = {
                    ...vehicle,
                    suite: suite.id,
                    building: (suite.building as IBuilding).id
                };
            }

            return ApiHelper.normalize(vehicle, Vehicle);
        });
    }

    private reduceData(data: IVehicle[]) {
        return data.map((row) => {
            const {brand, color, licensePlate, model, note, year} = row;

            return ApiHelper.normalize({brand, color, licensePlate, model, note, year}, Vehicle);
        });
    }
}

/**
 * Suite pet changes
 */
export class PetChanges extends Changes<IPet[]> {
    constructor(title: string, oldData: IPet[], newData: IPet[]) {
        super(title, oldData || [], newData || []);
        this.patched = Array.isArray(newData);
        this.changed = this.patched && Changes.isChanged(this.reduceData(oldData), this.reduceData(newData));
        this.ignore = !this.patched || !this.changed || Changes.includesEvery(this.reduceData(oldData), this.reduceData(newData));
        this.type = 'pet';
    }

    getPatch(suite: ISuite): IPet[] {
        return (this.newData as IPet[]).map(pet => {
            if(!pet.id) {
                pet = {
                    ...pet,
                    suite: suite.id,
                    building: (suite.building as IBuilding).id
                };
            }
            return ApiHelper.normalize(pet, Pet);
        });
    }

    private reduceData(data: IPet[]) {
        return data.map((row) => {
            const {name, breed, dateOfBirth, height, weight, petType, note} = row;

            return ApiHelper.normalize({name, breed, dateOfBirth, height, weight, petType, note}, Pet);
        });
    }
}

/**
 * Suite tenants (rent) changes
 */
export class TenantChanges extends Changes<IUser[]> {
    constructor(title: string, oldData: IUser[], newData: IUser[]) {
        super(title, oldData, newData);
        this.patched = Array.isArray(newData);
        this.ignore = !this.patched || !this.changed || Changes.includesEvery(oldData, newData);
        this.type = 'tenant';
    }
}



// WEBPACK FOOTER //
// /app/src/app/components/user-updates-modal/user-updates-modal.types.ts